import { useState } from "react";
import { cn } from "@/lib/utils";
import { EyebrowHeading, Section } from "./Section";
import { VideoEmbed } from "./VideoEmbed";

type Format = {
  key: string;
  label: string;
  tags: string[];
  title: string;
  body: string;
  length: string;
  videoId: string;
  videoTitle: string;
};

const formats: Format[] = [
  {
    key: "dok",
    label: "Intervjuformat",
    tags: ["Dokumentärt", "Kundcase", "Rapporter", "Thought Leadership"],
    title: "Autentiskt och trovärdigt",
    body: "Dokumentärkänsla som bygger förtroende och auktoritet. Perfekt för LinkedIn, kundcase och \"möt grundaren\"-innehåll, bakom kulisserna, och där värme och expertis måste synas på samma gång. Ett format som ofta kombinerar företagets/syftets vinkel med flera inspel som kunder, forskning eller liknande.",
    length: "Typisk längd: 30s / 60s / 90s · Klipps om till korta teasers för Reels och Shorts.",
    videoId: "BR5A8GfwkRc",
    videoTitle: "Pricka",
  },
  {
    key: "sketch",
    label: "Sketchformat",
    tags: ["Reels & TikTok", "Delbart", "Bjuder på sig själva", "Igenkännande"],
    title: "Lekfullt, igenkännande och delningsbart",
    body: "Korta, roliga klipp med branschhumor som får målgruppen att känna sig sedd — och dela vidare. Hook inom en sekund, punchline innan du hinner scrolla förbi.",
    length: "Typisk längd: 15–30s · Vertikalt (9:16) för Reels, TikTok och Shorts.",
    videoId: "tRzY9t6oR20",
    videoTitle: "Pricka",
  },
  {
    key: "reklam",
    label: "Reklamformat",
    tags: ["Konverterande", "Varumärke", "Brett format", "Prestanda"],
    title: "När budskap förmedlas",
    body: "Säljdriven film med tydlig CTA och rytm byggd för reklam och annonser som med fördel kan ha olika vinklar och distribueras över en mängd kanaler — TV, digitala annonser, streaming, sociala kanaler. Vi hjälper till med hela värdekedjan av publicering och formatframtagning för alla kanaler som behövs.",
    length: "Typisk längd: 15s / 30s / 60s · Flera varianter för A/B-test i Meta, TikTok och Google.",
    videoId: "BzHML38R2bs",
    videoTitle: "Pricka",
  },
  {
    key: "some",
    label: "Sociala Medier",
    tags: ["Annonser", "Flöde", "Kanalstyrt", "Engagerande", "Delningsbart"],
    title: "Organiska och betalda kanaler",
    body: "Varumärkesbyggande och kanalstyrt i kombination med budskap och konvertering i de sociala kanaler ni själva hanterar. Med leveranser mot A/B-tester för flöden, annonser, hooks, trovärdighet och budskap.",
    length: "Typisk längd: 15s / 20s / 30s · Klipps om till olika format för olika kanaler.",
    videoId: "yvojXrb41Aw",
    videoTitle: "Bolt — 20-sekunders kampanj",
  },
];

export function FormatShowcase() {
  const [active, setActive] = useState(formats[0].key);
  const current = formats.find((f) => f.key === active)!;

  return (
    <Section>
      <EyebrowHeading
        eyebrow="Format"
        title="Format som levererar i era kanaler"
        lede="Välj fritt mellan alla former av innehåll och format — reklamfilm, intervjuer, sketch, podcasts och SoMe med mera — från gång till gång."
      />

      <div className="mt-10 flex flex-wrap gap-2">
        {formats.map((f) => (
          <button
            key={f.key}
            onClick={() => setActive(f.key)}
            className={cn(
              "rounded-full border px-5 py-2.5 text-sm transition-colors",
              active === f.key
                ? "border-mint bg-mint text-mint-foreground"
                : "border-white/15 text-foreground/85 hover:border-white/40"
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-10 grid gap-8 rounded-3xl border border-white/10 bg-surface p-6 md:grid-cols-2 md:gap-10 md:p-10">
        <div>
          <VideoEmbed
            key={current.videoId}
            id={current.videoId}
            title={current.videoTitle}
            priority="auto"
          />
          <p className="mt-3 text-xs uppercase tracking-widest text-muted-foreground">
            Exempel · {current.videoTitle}
          </p>
        </div>
        <div className="flex flex-col">
          <div className="mb-5 flex flex-wrap gap-2">
            {current.tags.map((t) => (
              <span
                key={t}
                className="rounded-full border border-white/15 px-3 py-1 text-xs text-foreground/80"
              >
                {t}
              </span>
            ))}
          </div>
          <h3 className="text-balance text-2xl font-semibold leading-tight md:text-3xl">
            {current.title}
          </h3>
          <p className="mt-5 text-pretty text-base leading-relaxed text-foreground/85">
            {current.body}
          </p>
          <p className="mt-6 text-sm text-muted-foreground">{current.length}</p>
        </div>
      </div>
    </Section>
  );
}
